import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberMigrationStatusOrNoneSchema } from '../member-migration/read-only';

// The staff read behind Atlas's Pelago 1 read-only state: for each ref the
// caller already holds (a Galapagos member id or a backend2 sub), whether that
// member's migration has started.
//
// A batch read because two Atlas surfaces need it: the member view asks about
// one member, and the member list's bulk actions ask about a whole selection at
// submit time.
//
// Items echo the requested ref and nothing else. In particular there is no
// Galapagos member id: the route is exempt from member-scope evaluation, so
// returning the id resolved from a backend2 sub would hand any caller with the
// read group a sub → member-id crosswalk.

export const MEMBER_MIGRATION_STATUS_MAX_REFS = 100;
/** A Galapagos id or backend2 sub is far shorter; the cap bounds the IN-list payload. */
export const MEMBER_MIGRATION_STATUS_MAX_REF_LENGTH = 256;

export const memberMigrationStatusRequestSchema = z
  .object({
    refs: z
      .array(z.string().min(1).max(MEMBER_MIGRATION_STATUS_MAX_REF_LENGTH))
      .min(1)
      .max(MEMBER_MIGRATION_STATUS_MAX_REFS),
  })
  .strict();
export type MemberMigrationStatusRequest = z.infer<typeof memberMigrationStatusRequestSchema>;

// Deliberately NOT `.strict()` at either level (ADR-135 Rule 1): Atlas parses
// this response, and a strict item would turn a future additive field into a
// failed parse.
export const memberMigrationStatusItemSchema = z.object({
  ref: z.string(),
  migrationStatus: memberMigrationStatusOrNoneSchema,
  pelago1ReadOnly: z.boolean(),
});
export type MemberMigrationStatusItem = z.infer<typeof memberMigrationStatusItemSchema>;

export const memberMigrationStatusDataSchema = z.object({
  statuses: z.array(memberMigrationStatusItemSchema),
});
export type MemberMigrationStatusData = z.infer<typeof memberMigrationStatusDataSchema>;

export const memberMigrationStatusResponseSchema = apiEnvelope(memberMigrationStatusDataSchema);
export type MemberMigrationStatusResponse = z.infer<typeof memberMigrationStatusResponseSchema>;
